// The hands-free conversation, relayed through this app.
//
// # Why a socket to this app, and not to the provider
//
// A record-transcribe-think-speak round trip returns the whole utterance at the
// end, so the wait grows with the length of the answer, and there is no way for
// a child to interrupt. A real-time session has neither problem. It runs on
// Vertex AI in the EU, which issues no credential a browser could hold, so the
// server holds the session and this socket carries the audio to it: 16-bit mono
// PCM at the microphone's rate up, at 24 kHz down.
//
// What the browser opens the socket with is a single-use ticket minted for one
// dialogue. The model, the voice, the rules and the conversation so far are
// the server's; so is the transcript — each completed turn is written there,
// and the socket says so with a `turn` event.

import { BASE, unwrap } from '../api/client';
import { liveToken } from '../api/gen';

export type LiveStatus = 'connecting' | 'listening' | 'speaking' | 'idle';

export interface LiveHandlers {
  onStatus?: (s: LiveStatus) => void;
  /** A turn was persisted; the transcript on screen is now behind. */
  onTurn?: () => void;
  onError?: (message: string) => void;
}

export interface LiveHandle {
  stop: () => void;
}

export interface LiveOptions {
  orgId: string;
}

// What the socket says besides audio.
interface LiveEvent {
  type: 'ready' | 'interrupted' | 'turn' | 'error';
  message?: string;
}

const INPUT_RATE = 16000;

function audioContextCtor(): typeof AudioContext | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext;
}

export function liveSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof WebSocket !== 'undefined' &&
    audioContextCtor() !== undefined
  );
}

function socketURL(sessionId: string, ticket: string, rate: number): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const q = new URLSearchParams({ ticket, rate: String(rate) });
  return `${proto}//${window.location.host}${BASE}/sessions/${sessionId}/live?${q}`;
}

export async function startLiveSession(
  sessionId: string,
  h: LiveHandlers,
  opts: LiveOptions,
): Promise<LiveHandle> {
  const AC = audioContextCtor();
  if (!AC) throw new Error('audio not supported');

  h.onStatus?.('connecting');

  const ticket = await unwrap(
    liveToken({ path: { id: sessionId }, headers: { 'X-Org-ID': opts.orgId } }),
  );
  if (!ticket) throw new Error('kein Sprach-Ticket');
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

  // --- what comes down: PCM at the ticket's rate, scheduled gaplessly ---
  const playCtx = new AC();
  let nextPlayTime = 0;
  const sources = new Set<AudioBufferSourceNode>();
  const stopPlayback = () => {
    for (const s of sources) {
      try {
        s.stop();
      } catch {
        // already stopped
      }
    }
    sources.clear();
    nextPlayTime = 0;
  };
  const playPcm = (bytes: ArrayBuffer) => {
    const count = Math.floor(bytes.byteLength / 2);
    if (count === 0) return;
    const view = new DataView(bytes, 0, count * 2);
    const f32 = new Float32Array(count);
    for (let i = 0; i < count; i++) f32[i] = view.getInt16(i * 2, true) / 0x8000;
    const buf = playCtx.createBuffer(1, count, ticket.outputRate);
    buf.getChannelData(0).set(f32);
    const src = playCtx.createBufferSource();
    src.buffer = buf;
    src.connect(playCtx.destination);
    const startAt = Math.max(playCtx.currentTime, nextPlayTime);
    src.start(startAt);
    nextPlayTime = startAt + buf.duration;
    sources.add(src);
    src.onended = () => {
      sources.delete(src);
      if (sources.size === 0 && !stopped) h.onStatus?.('listening');
    };
  };

  // --- what goes up: the microphone ---
  const micCtx = new AC({ sampleRate: INPUT_RATE });
  const micSource = micCtx.createMediaStreamSource(stream);
  const processor = micCtx.createScriptProcessor(4096, 1, 1);
  const mute = micCtx.createGain();
  mute.gain.value = 0;
  // Some browsers ignore the requested rate, so declare the one in force.
  const micRate = micCtx.sampleRate;

  const socket = new WebSocket(socketURL(sessionId, ticket.ticket, micRate));
  socket.binaryType = 'arraybuffer';
  let stopped = false;

  const cleanup = () => {
    if (stopped) return;
    stopped = true;
    processor.onaudioprocess = null;
    try {
      processor.disconnect();
      micSource.disconnect();
      mute.disconnect();
    } catch {
      // already torn down
    }
    stream.getTracks().forEach((t) => t.stop());
    stopPlayback();
    if (micCtx.state !== 'closed') void micCtx.close().catch(() => {});
    if (playCtx.state !== 'closed') void playCtx.close().catch(() => {});
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close();
    }
    h.onStatus?.('idle');
  };

  socket.onmessage = (e: MessageEvent) => {
    if (stopped) return;
    if (e.data instanceof ArrayBuffer) {
      h.onStatus?.('speaking');
      playPcm(e.data);
      return;
    }
    let ev: LiveEvent;
    try {
      ev = JSON.parse(e.data as string) as LiveEvent;
    } catch {
      return;
    }
    switch (ev.type) {
      case 'ready':
        h.onStatus?.('listening');
        break;
      // The child spoke over the tutor. Stop talking and listen — which is
      // what a person does, and what makes this feel like a conversation.
      case 'interrupted':
        stopPlayback();
        h.onStatus?.('listening');
        break;
      case 'turn':
        h.onTurn?.();
        break;
      case 'error':
        h.onError?.(ev.message ?? 'Die Sprachverbindung wurde unterbrochen.');
        cleanup();
        break;
    }
  };
  socket.onerror = () => {
    if (!stopped) h.onError?.('Die Sprachverbindung wurde unterbrochen.');
    cleanup();
  };
  socket.onclose = () => cleanup();

  await new Promise<void>((resolve, reject) => {
    socket.addEventListener('open', () => resolve(), { once: true });
    socket.addEventListener('close', () => reject(new Error('Die Sprachverbindung wurde abgelehnt.')), {
      once: true,
    });
  });

  // A click got us here, so both contexts should already be running; resume in
  // case a browser started them suspended.
  if (micCtx.state === 'suspended') await micCtx.resume().catch(() => {});
  if (playCtx.state === 'suspended') await playCtx.resume().catch(() => {});

  processor.onaudioprocess = (e) => {
    if (stopped || socket.readyState !== WebSocket.OPEN) return;
    const input = e.inputBuffer.getChannelData(0);
    const pcm = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    socket.send(pcm.buffer);
  };
  micSource.connect(processor);
  processor.connect(mute);
  mute.connect(micCtx.destination);

  return { stop: cleanup };
}
